import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { aMapZoomNextListingLoad, MapFiltersType, useSetAtom } from '~/atoms'
import {
  MapAddressSearchFilter,
  MapCityFilter,
  MapCountyFilter,
  MapPostalCodeFilter,
  MapStateFilter,
  MapSubmarketsFilter,
  MapZoningFilter,
} from '.'

async function getLocationFilters() {
  const response = await fetch('/api/filters', {
    method: 'POST',
    body: JSON.stringify({}),
  })
  const result = await response.json()
  if (!result.success) {
    console.error('Error getting location filters', result.error)
    throw new Error(`There was a problem fetching results: ${result.error}`)
  }
  return result
}

export default function LocationFilters({
  filters,
  updateFilter,
}: {
  filters: MapFiltersType
  updateFilter: (updates: Record<string, any>) => void
}) {
  const setMapZoomNextListingLoad = useSetAtom(aMapZoomNextListingLoad)
  // Loads all location filters from organization in the api endpoint
  const filtersQuery = useQuery({
    queryKey: ['location-filters', 'disable-cache'],
    queryFn: () => getLocationFilters(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  const zoningValues = useMemo(
    () => filtersQuery.data?.zoning || [],
    [filtersQuery.data]
  )

  const cityValues = useMemo(
    () => filtersQuery.data?.cities || [],
    [filtersQuery.data]
  )

  const countyValues = useMemo(
    () => filtersQuery.data?.counties || [],
    [filtersQuery.data]
  )

  const postalCodeValues = useMemo(
    () => filtersQuery.data?.postalCodes || [],
    [filtersQuery.data]
  )

  const stateValues = useMemo(
    () => filtersQuery.data?.states || [],
    [filtersQuery.data]
  )

  const updateFiltersWithZoomTo = useCallback(
    (updates: Record<string, any>) => {
      updateFilter(updates)
      if (
        Object.keys(updates).length > 1 ||
        (Object.keys(updates).length === 1 && Object.values(updates)[0])
      ) {
        setMapZoomNextListingLoad(true)
      }
    },
    [updateFilter, setMapZoomNextListingLoad]
  )

  return (
    <>
      <MapAddressSearchFilter
        addressSearch={filters.addressSearch}
        updateFilter={updateFiltersWithZoomTo}
        validCities={cityValues}
        validCounties={countyValues}
        validStates={stateValues}
        validPostalCodes={postalCodeValues}
      />

      <MapCityFilter
        cityValues={cityValues}
        filterCity={filters.cities || []}
        updateFilter={updateFiltersWithZoomTo}
      />

      <MapCountyFilter
        countyValues={countyValues}
        filterCounty={filters.counties || []}
        updateFilter={updateFiltersWithZoomTo}
      />

      <MapPostalCodeFilter
        postalCodeValues={postalCodeValues}
        filterPostalCode={filters.postalCodes || []}
        updateFilter={updateFiltersWithZoomTo}
      />
      {stateValues.length > 1 && (
        <MapStateFilter
          stateValues={stateValues}
          filterState={filters.states || []}
          updateFilter={updateFiltersWithZoomTo}
        />
      )}

      <MapSubmarketsFilter
        submarkets={filters.submarkets || []}
        updateFilter={updateFilter}
      />
      <MapZoningFilter
        zoningValues={zoningValues}
        filterZoning={filters.zoning || []}
        updateFilter={updateFilter}
      />
    </>
  )
}
